import type { AccessGateway } from '../application/accessGateway';
import type {
  AccessErrorCode,
  AccessResult,
  InvitationCredentials,
} from '../domain/access';

const env = import.meta.env as Record<string, string | undefined>;
const isTestMode = env.MODE === 'test';
const supabaseUrl = (
  env.VITE_SUPABASE_URL ??
  (isTestMode ? 'https://alpha-test.invalid' : '')
).replace(/\/$/u, '');
const publishableKey =
  env.VITE_SUPABASE_PUBLISHABLE_KEY ??
  (isTestMode ? 'alpha-test-publishable-key' : '');
const founderEmail = (
  env.VITE_ALPHA_FOUNDER_EMAIL ??
  (isTestMode ? 'founder@example.test' : '')
).trim().toLowerCase();

function requireRuntimeConfig(): void {
  if (supabaseUrl === '' || publishableKey === '') {
    throw new Error(
      'Alpha•MLS runtime configuration is missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY.',
    );
  }
}

const sessionKey = 'alpha-mls:supabase-session:v1';
const pendingKey = 'alpha-mls:pending-access:v1';

interface StoredSession {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresAt: number;
}

type PendingAccess =
  | { readonly kind: 'founder' }
  | {
      readonly kind: 'invitation';
      readonly email: string;
      readonly token: string;
    };

interface AuthUser {
  readonly id: string;
  readonly email?: string;
  readonly user_metadata?: Record<string, unknown>;
}

function errorResult(code: AccessErrorCode, message: string): AccessResult {
  return { ok: false, error: { code, message } };
}

function pendingResult(message: string): AccessResult {
  return {
    ok: false,
    pending: true,
    error: {
      code: 'IDENTITY_UNAVAILABLE',
      message,
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readStoredSession(): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(sessionKey);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !isRecord(parsed) ||
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.refreshToken !== 'string' ||
      typeof parsed.expiresAt !== 'number'
    ) {
      window.localStorage.removeItem(sessionKey);
      return null;
    }

    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    window.localStorage.removeItem(sessionKey);
    return null;
  }
}

function writeStoredSession(session: StoredSession): void {
  window.localStorage.setItem(sessionKey, JSON.stringify(session));
}

function clearStoredSession(): void {
  window.localStorage.removeItem(sessionKey);
}

function readPendingAccess(): PendingAccess | null {
  try {
    const raw = window.sessionStorage.getItem(pendingKey);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);

    if (!isRecord(parsed) || typeof parsed.kind !== 'string') return null;

    if (parsed.kind === 'founder') {
      return { kind: 'founder' };
    }

    if (
      parsed.kind === 'invitation' &&
      typeof parsed.email === 'string' &&
      typeof parsed.token === 'string'
    ) {
      return {
        kind: 'invitation',
        email: parsed.email,
        token: parsed.token,
      };
    }

    return null;
  } catch {
    return null;
  }
}

function writePendingAccess(pending: PendingAccess): void {
  window.sessionStorage.setItem(pendingKey, JSON.stringify(pending));
}

function clearPendingAccess(): void {
  window.sessionStorage.removeItem(pendingKey);
}

function captureImplicitAuthCallback(): void {
  if (!window.location.hash.includes('access_token=')) return;

  const params = new URLSearchParams(window.location.hash.slice(1));
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const expiresAtParam = params.get('expires_at');
  const expiresInParam = params.get('expires_in');

  if (accessToken === null || refreshToken === null) return;

  const expiresAt =
    expiresAtParam === null
      ? Math.floor(Date.now() / 1000) + Number(expiresInParam ?? '3600')
      : Number(expiresAtParam);

  if (!Number.isFinite(expiresAt)) return;

  writeStoredSession({ accessToken, refreshToken, expiresAt });
  window.history.replaceState(null, document.title, window.location.pathname);
}

async function parseJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function optionalSignal(
  signal: AbortSignal | undefined,
): Pick<RequestInit, 'signal'> | object {
  return signal === undefined ? {} : { signal };
}

async function authFetch(
  path: string,
  init: RequestInit,
  signal?: AbortSignal,
): Promise<Response> {
  requireRuntimeConfig();
  const headers = new Headers(init.headers);
  headers.set('apikey', publishableKey);
  headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');

  return fetch(`${supabaseUrl}${path}`, {
    ...init,
    ...optionalSignal(signal),
    headers,
  });
}

async function refreshSession(
  session: StoredSession,
  signal?: AbortSignal,
): Promise<StoredSession | null> {
  const response = await authFetch(
    '/auth/v1/token?grant_type=refresh_token',
    {
      method: 'POST',
      body: JSON.stringify({ refresh_token: session.refreshToken }),
    },
    signal,
  );

  if (!response.ok) {
    clearStoredSession();
    return null;
  }

  const payload = await parseJson(response);
  if (
    !isRecord(payload) ||
    typeof payload.access_token !== 'string' ||
    typeof payload.refresh_token !== 'string' ||
    typeof payload.expires_in !== 'number'
  ) {
    clearStoredSession();
    return null;
  }

  const refreshed = {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt: Math.floor(Date.now() / 1000) + payload.expires_in,
  };

  writeStoredSession(refreshed);
  return refreshed;
}

async function currentSession(
  signal?: AbortSignal,
): Promise<StoredSession | null> {
  const session = readStoredSession();
  if (session === null) return null;

  if (session.expiresAt - 30 > Math.floor(Date.now() / 1000)) {
    return session;
  }

  return refreshSession(session, signal);
}

async function currentUser(
  session: StoredSession,
  signal?: AbortSignal,
): Promise<AuthUser | null> {
  const response = await authFetch(
    '/auth/v1/user',
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${session.accessToken}` },
    },
    signal,
  );

  if (!response.ok) return null;

  const payload = await parseJson(response);
  if (!isRecord(payload) || typeof payload.id !== 'string') return null;

  return {
    id: payload.id,
    ...(typeof payload.email === 'string' ? { email: payload.email } : {}),
    ...(isRecord(payload.user_metadata)
      ? { user_metadata: payload.user_metadata }
      : {}),
  };
}

async function requestMagicLink(
  email: string,
  shouldCreateUser: boolean,
  signal?: AbortSignal,
): Promise<boolean> {
  const redirectTo = `${window.location.origin}/`;
  const response = await authFetch(
    `/auth/v1/otp?redirect_to=${encodeURIComponent(redirectTo)}`,
    {
      method: 'POST',
      body: JSON.stringify({
        email,
        create_user: shouldCreateUser,
      }),
    },
    signal,
  );

  return response.ok;
}

function displayName(user: AuthUser): string {
  const configured = user.user_metadata?.display_name;
  if (typeof configured === 'string' && configured.trim().length > 0) {
    return configured.trim();
  }

  return user.email?.split('@')[0] ?? 'Usuario';
}

async function invitationFunction(
  payload: Record<string, unknown>,
  accessToken: string | null,
  signal?: AbortSignal,
): Promise<Response> {
  requireRuntimeConfig();
  const headers: Record<string, string> = {
    apikey: publishableKey,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (accessToken !== null) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return fetch(`${supabaseUrl}/functions/v1/access-invitation`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    ...optionalSignal(signal),
  });
}

function invitationFailure(
  status: number,
  payload: unknown,
): AccessResult {
  const code =
    isRecord(payload) && typeof payload.code === 'string'
      ? payload.code
      : '';

  if (status === 410 || code === 'INVITATION_EXPIRED') {
    return errorResult(
      'INVITATION_EXPIRED',
      'La invitación venció. Solicita una nueva invitación.',
    );
  }

  if (status === 401 || code === 'IDENTITY_REJECTED') {
    return errorResult(
      'IDENTITY_REJECTED',
      'La sesión no corresponde con la identidad invitada.',
    );
  }

  if (status >= 500 || code === 'SERVICE_UNAVAILABLE') {
    return errorResult(
      'NETWORK_ERROR',
      'El servicio de acceso no está disponible en este momento.',
    );
  }

  return errorResult(
    'INVITATION_INVALID',
    'La invitación no es válida o ya fue utilizada.',
  );
}

async function founderIdentity(
  session: StoredSession,
  user: AuthUser,
  signal?: AbortSignal,
): Promise<AccessResult> {
  const filter = new URLSearchParams({
    profile_id: `eq.${user.id}`,
    scope_type: 'eq.global',
    role: 'in.(founder,admin)',
    select: 'role',
    limit: '1',
  });

  const response = await authFetch(
    `/rest/v1/role_assignments?${filter.toString()}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${session.accessToken}` },
    },
    signal,
  );

  if (!response.ok) {
    return errorResult(
      'IDENTITY_REJECTED',
      'La identidad existe, pero no tiene autorización de Fundador.',
    );
  }

  const roles = await parseJson(response);
  if (!Array.isArray(roles) || roles.length === 0) {
    return errorResult(
      'IDENTITY_REJECTED',
      'La identidad existe, pero no tiene autorización de Fundador.',
    );
  }

  return {
    ok: true,
    identity: {
      actor: 'founder',
      displayName: displayName(user),
      redirectTo: '/dashboard',
    },
  };
}

async function completeFounderSession(
  session: StoredSession,
  user: AuthUser,
  signal?: AbortSignal,
): Promise<AccessResult> {
  const response = await invitationFunction(
    { mode: 'complete-founder' },
    session.accessToken,
    signal,
  );

  const payload = await parseJson(response);
  if (!response.ok) {
    return errorResult(
      'IDENTITY_REJECTED',
      'La sesión no corresponde con la identidad fundadora.',
    );
  }

  if (
    !isRecord(payload) ||
    payload.ok !== true ||
    !isRecord(payload.identity) ||
    payload.identity.actor !== 'founder'
  ) {
    return errorResult(
      'UNEXPECTED_RESPONSE',
      'El servicio de identidad devolvió una respuesta inválida.',
    );
  }

  return founderIdentity(session, user, signal);
}

export class SupabaseAccessGateway implements AccessGateway {
  constructor() {
    captureImplicitAuthCallback();
  }

  async beginFounderAccess(signal?: AbortSignal): Promise<AccessResult> {
    try {
      const session = await currentSession(signal);

      if (session !== null) {
        const user = await currentUser(session, signal);
        if (user === null) {
          clearStoredSession();
          return errorResult(
            'IDENTITY_REJECTED',
            'La sesión de identidad ya no es válida.',
          );
        }

        return await completeFounderSession(session, user, signal);
      }

      writePendingAccess({ kind: 'founder' });

      const response = await invitationFunction(
        {
          mode: 'request-founder',
          redirectTo: `${window.location.origin}/`,
        },
        null,
        signal,
      );

      const payload = await parseJson(response);
      if (!response.ok) {
        const code =
          isRecord(payload) && typeof payload.code === 'string'
            ? payload.code
            : '';

        if (response.status === 429 || code === 'EMAIL_RATE_LIMITED') {
          return pendingResult(
            'El enlace seguro ya fue solicitado. Revisa tu correo antes de pedir otro.',
          );
        }

        clearPendingAccess();

        if (code === 'SERVICE_UNAVAILABLE') {
          return errorResult(
            'NETWORK_ERROR',
            'Supabase Auth no pudo iniciar el acceso en este momento.',
          );
        }

        return errorResult(
          'IDENTITY_UNAVAILABLE',
          'La identidad fundadora de staging todavía no está configurada.',
        );
      }

      return pendingResult(
        'Enviamos el enlace seguro de acceso a la identidad fundadora.',
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      return errorResult(
        'NETWORK_ERROR',
        'No fue posible contactar Supabase Auth. Intenta nuevamente.',
      );
    }
  }

  async verifyFounderCode(
    code: string,
    signal?: AbortSignal,
  ): Promise<AccessResult> {
    try {
      if (founderEmail === '') {
        return errorResult(
          'IDENTITY_UNAVAILABLE',
          'La identidad fundadora no está configurada en este entorno.',
        );
      }

      const response = await authFetch(
        '/auth/v1/verify',
        {
          method: 'POST',
          body: JSON.stringify({
            email: founderEmail,
            token: code.trim(),
            type: 'magiclink',
          }),
        },
        signal,
      );

      const payload = await parseJson(response);

      if (
        !response.ok ||
        !isRecord(payload) ||
        typeof payload.access_token !== 'string' ||
        typeof payload.refresh_token !== 'string'
      ) {
        return errorResult(
          'IDENTITY_REJECTED',
          'El código de acceso no es válido o ya venció.',
        );
      }

      const expiresAt =
        typeof payload.expires_at === 'number'
          ? payload.expires_at
          : Math.floor(Date.now() / 1000) +
            (typeof payload.expires_in === 'number' ? payload.expires_in : 3600);

      const session: StoredSession = {
        accessToken: payload.access_token,
        refreshToken: payload.refresh_token,
        expiresAt,
      };
      writeStoredSession(session);

      const user = await currentUser(session, signal);
      if (user === null) {
        clearStoredSession();
        return errorResult(
          'IDENTITY_REJECTED',
          'La sesión de identidad no pudo verificarse.',
        );
      }

      const result = await completeFounderSession(session, user, signal);
      if (result.ok) clearPendingAccess();
      return result;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      return errorResult(
        'NETWORK_ERROR',
        'No fue posible verificar el código de acceso.',
      );
    }
  }

  async redeemInvitation(
    credentials: InvitationCredentials,
    signal?: AbortSignal,
  ): Promise<AccessResult> {
    try {
      const validateResponse = await invitationFunction(
        {
          mode: 'validate',
          email: credentials.email,
          token: credentials.token,
        },
        null,
        signal,
      );

      const validatePayload = await parseJson(validateResponse);
      if (!validateResponse.ok) {
        return invitationFailure(validateResponse.status, validatePayload);
      }

      writePendingAccess({
        kind: 'invitation',
        email: credentials.email,
        token: credentials.token,
      });

      const session = await currentSession(signal);
      if (session !== null) {
        const user = await currentUser(session, signal);
        if (user?.email?.trim().toLowerCase() === credentials.email) {
          return await this.completeInvitation(
            session,
            credentials,
            signal,
          );
        }

        clearStoredSession();
      }

      const sent = await requestMagicLink(credentials.email, true, signal);
      if (!sent) {
        clearPendingAccess();
        return errorResult(
          'IDENTITY_UNAVAILABLE',
          'La invitación es válida, pero no fue posible iniciar la sesión segura.',
        );
      }

      return pendingResult(
        'Invitación verificada. Revisa tu correo para completar el acceso seguro.',
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      return errorResult(
        'NETWORK_ERROR',
        'No fue posible contactar el servicio de acceso. Intenta nuevamente.',
      );
    }
  }

  async resumePendingAccess(
    signal?: AbortSignal,
  ): Promise<AccessResult | null> {
    const pending = readPendingAccess();
    const session = await currentSession(signal);
    if (session === null) return null;

    const user = await currentUser(session, signal);
    if (user === null) {
      clearStoredSession();
      return errorResult(
        'IDENTITY_REJECTED',
        'La sesión de identidad ya no es válida.',
      );
    }

    if (pending === null) {
      const founder = await completeFounderSession(session, user, signal);
      return founder.ok ? founder : null;
    }

    if (pending.kind === 'founder') {
      const result = await completeFounderSession(session, user, signal);
      if (result.ok) {
        clearPendingAccess();
        return result;
      }

      clearStoredSession();
      clearPendingAccess();
      return result;
    }

    if (user.email?.trim().toLowerCase() !== pending.email) {
      clearStoredSession();
      clearPendingAccess();
      return errorResult(
        'IDENTITY_REJECTED',
        'La sesión no corresponde con el correo invitado.',
      );
    }

    return this.completeInvitation(session, pending, signal);
  }

  async resolveCurrentIdentity(
    signal?: AbortSignal,
  ): Promise<AccessResult> {
    const session = await currentSession(signal);
    if (session === null) {
      return errorResult(
        'IDENTITY_UNAVAILABLE',
        'No existe una sesión autenticada.',
      );
    }

    const user = await currentUser(session, signal);
    if (user === null) {
      clearStoredSession();
      return errorResult(
        'IDENTITY_REJECTED',
        'La sesión de identidad ya no es válida.',
      );
    }

    const founder = await founderIdentity(session, user, signal);
    if (founder.ok) return founder;

    const response = await invitationFunction(
      { mode: 'session-status' },
      session.accessToken,
      signal,
    );

    if (!response.ok) {
      return errorResult(
        'IDENTITY_REJECTED',
        'La sesión existe, pero no tiene autorización para Alpha•MLS.',
      );
    }

    const payload = await parseJson(response);
    if (
      !isRecord(payload) ||
      payload.ok !== true ||
      !isRecord(payload.identity) ||
      payload.identity.actor !== 'invited-client' ||
      typeof payload.identity.displayName !== 'string'
    ) {
      return errorResult(
        'UNEXPECTED_RESPONSE',
        'El servicio de identidad devolvió una respuesta inválida.',
      );
    }

    return {
      ok: true,
      identity: {
        actor: 'invited-client',
        displayName: payload.identity.displayName,
        redirectTo: '/dashboard',
      },
    };
  }

  async signOut(signal?: AbortSignal): Promise<void> {
    const session = readStoredSession();

    if (session !== null) {
      try {
        await authFetch(
          '/auth/v1/logout',
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.accessToken}` },
          },
          signal,
        );
      } finally {
        clearStoredSession();
        clearPendingAccess();
      }
      return;
    }

    clearStoredSession();
    clearPendingAccess();
  }

  private async completeInvitation(
    session: StoredSession,
    credentials: InvitationCredentials,
    signal?: AbortSignal,
  ): Promise<AccessResult> {
    const response = await invitationFunction(
      {
        mode: 'complete',
        email: credentials.email,
        token: credentials.token,
      },
      session.accessToken,
      signal,
    );

    const payload = await parseJson(response);
    if (!response.ok) {
      return invitationFailure(response.status, payload);
    }

    if (
      !isRecord(payload) ||
      payload.ok !== true ||
      !isRecord(payload.identity) ||
      payload.identity.actor !== 'invited-client' ||
      typeof payload.identity.displayName !== 'string'
    ) {
      return errorResult(
        'UNEXPECTED_RESPONSE',
        'El servicio de identidad devolvió una respuesta inválida.',
      );
    }

    clearPendingAccess();

    return {
      ok: true,
      identity: {
        actor: 'invited-client',
        displayName: payload.identity.displayName,
        redirectTo: '/dashboard',
      },
    };
  }
}
